import { createSlice } from "@reduxjs/toolkit";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { Message } from "./models/Message";
import { channelKey } from "./models/MessagesChannel";
import { MessageThread } from "./models/MessageThread";
import { SessionCard } from "./models/SessionCard";
import {
  deleteMessage,
  fetchMessages,
  fetchSessionCard,
  fetchOlderMessages,
  fetchThreads,
  fetchUnreadCount,
  markThreadRead,
  openDirectThread,
  openSubjectThread,
  sendMessage,
  setThreadMuted,
  setThreadStaffOnly,
} from "./slice-extensions/messages-slice-extension";
import { reportItem } from "./slice-extensions/reports-slice-extension";
import { mergeMessages } from "./utils/mergeMessages";

export interface ThreadMessagesState {
  /** Newest first. */
  items: Message[];
  /** Cursor for the next older page; null once the start is reached. */
  nextBefore: string | null;
  status: LoadingStatus;
}

export interface MessagesState {
  threadsByChannel: Record<string, MessageThread[]>;
  threadsStatus: LoadingStatus;
  messagesByThreadId: Record<string, ThreadMessagesState>;
  unreadByChannel: Record<string, number>;
  sessionCardsByThreadId: Record<string, SessionCard>;
  sendStatus: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: MessagesState = {
  threadsByChannel: {},
  threadsStatus: LoadingStatus.Idle,
  messagesByThreadId: {},
  unreadByChannel: {},
  sessionCardsByThreadId: {},
  sendStatus: LoadingStatus.Idle,
};

const threadState = (state: MessagesState, threadId: string): ThreadMessagesState =>
  (state.messagesByThreadId[threadId] ??= { items: [], nextBefore: null, status: LoadingStatus.Idle });

const upsertThread = (threads: MessageThread[] | undefined, thread: MessageThread): MessageThread[] => [
  thread,
  ...(threads ?? []).filter((t) => t.id !== thread.id),
];

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchThreads.pending, (state) => {
        state.threadsStatus = LoadingStatus.Loading;
      })
      .addCase(fetchThreads.fulfilled, (state, action) => {
        state.threadsStatus = LoadingStatus.Idle;
        state.threadsByChannel[channelKey(action.meta.arg)] = action.payload;
      })
      .addCase(fetchThreads.rejected, (state, action) => {
        state.threadsStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      })
      .addCase(openDirectThread.fulfilled, (state, action) => {
        state.threadsByChannel.staff = upsertThread(state.threadsByChannel.staff, action.payload);
      })
      .addCase(openSubjectThread.fulfilled, (state, action) => {
        state.threadsByChannel.staff = upsertThread(state.threadsByChannel.staff, action.payload);
      })
      .addCase(fetchSessionCard.fulfilled, (state, action) => {
        state.sessionCardsByThreadId[action.meta.arg.threadId] = action.payload;
      });

    builder
      .addCase(fetchMessages.pending, (state, action) => {
        const thread = threadState(state, action.meta.arg.threadId);
        if (thread.items.length === 0) thread.status = LoadingStatus.Loading;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        const thread = threadState(state, action.meta.arg.threadId);
        const firstLoad = thread.items.length === 0;
        thread.items = mergeMessages(thread.items, action.payload.items);
        if (firstLoad) thread.nextBefore = action.payload.nextBefore;
        thread.status = LoadingStatus.Idle;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        threadState(state, action.meta.arg.threadId).status = LoadingStatus.Failed;
        state.lastError = action.payload;
      })
      .addCase(fetchOlderMessages.fulfilled, (state, action) => {
        const thread = threadState(state, action.meta.arg.threadId);
        thread.items = mergeMessages(thread.items, action.payload.items);
        thread.nextBefore = action.payload.nextBefore;
      });

    builder
      .addCase(sendMessage.pending, (state) => {
        state.sendStatus = LoadingStatus.Loading;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sendStatus = LoadingStatus.Idle;
        const thread = threadState(state, action.meta.arg.threadId);
        thread.items = mergeMessages(thread.items, [action.payload]);
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sendStatus = LoadingStatus.Failed;
        state.lastError = action.payload;
      });

    builder
      .addCase(markThreadRead.fulfilled, (state, action) => {
        const key = channelKey(action.meta.arg.channel);
        const thread = state.threadsByChannel[key]?.find((t) => t.id === action.meta.arg.threadId);
        if (!thread) return;
        // A muted thread never counted in the badge.
        if (!thread.isMuted) {
          state.unreadByChannel[key] = Math.max(0, (state.unreadByChannel[key] ?? 0) - thread.unreadCount);
        }
        thread.unreadCount = 0;
      })
      .addCase(setThreadMuted.fulfilled, (state, action) => {
        const { channel, threadId, muted } = action.meta.arg;
        const thread = state.threadsByChannel[channelKey(channel)]?.find((t) => t.id === threadId);
        if (thread) thread.isMuted = muted;
      })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        const { threadId, messageId } = action.meta.arg;
        const message = state.messagesByThreadId[threadId]?.items.find((m) => m.id === messageId);
        if (!message) return;
        // The server cleared it the same way; the next fetch brings the same placeholder.
        Object.assign(message, { body: "", payload: null, card: null, attachments: [], canDelete: false, deletedAt: new Date() });
      })
      .addCase(reportItem.fulfilled, (state, action) => {
        const { targetKind, targetId, threadId } = action.meta.arg.request;
        if (targetKind !== "Message" || !threadId) return;
        const message = state.messagesByThreadId[threadId]?.items.find((m) => m.id === targetId);
        // Hidden for the reporter only; the next fetch brings the same "Reported" row.
        if (message) Object.assign(message, { body: "", payload: null, card: null, attachments: [], reportedByMe: true });
      })
      .addCase(setThreadStaffOnly.fulfilled, (state, action) => {
        state.threadsByChannel.staff = (state.threadsByChannel.staff ?? []).map((t) =>
          t.id === action.payload.id ? action.payload : t,
        );
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadByChannel[channelKey(action.meta.arg)] = action.payload;
      });
  },
});

export default messagesSlice.reducer;
